import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API } from "../lib/api";
import { useBranding } from "../lib/branding";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const brand = useBranding();
  const brandLogo = brand.login ? `${API}${brand.login}` : (brand.product ? `${API}${brand.product}` : null);
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden p-12 lg:flex" style={{ background: "linear-gradient(180deg,#25102F 0%,#35133F 35%,#4A1653 70%,#6A1D68 100%)" }}>
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/11924298/pexels-photo-11924298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex items-center gap-3" />
        <div className="relative z-10 max-w-md">
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.25em] text-orange-300">Your CEO's Voice</div>
          <h2 className="font-display text-4xl font-bold leading-tight text-white">
            "Work with Integrity.<br />Record with Accuracy.<br />Deliver with Excellence."
          </h2>
          <div className="mt-8 border-l-4 border-orange-400 pl-4">
            <div className="mb-1 text-xs font-semibold uppercase tracking-[0.25em] text-teal-200/90">At Radiants</div>
            <p className="font-display text-2xl font-bold leading-snug text-white">
              "Every Entry Matters.<br />Every Project Counts.<br />Every Team Member Contributes."
            </p>
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-xs text-teal-200/70">
          <ShieldCheck className="h-4 w-4" /> A Product of Radiants Civiletric Pvt Ltd
        </div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center bg-gradient-to-br from-white via-white to-orange-50/40 p-6 lg:w-1/2">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          {/* Brand block */}
          <div className="mb-8 flex flex-col items-center text-center">
            {brandLogo
              ? <img src={brandLogo} alt="Radiants" className="h-20 max-w-[260px] object-contain" />
              : <div className="grid h-16 w-16 place-items-center rounded-xl bg-[#7C3AED] font-display text-2xl font-bold text-white">MF</div>}
            <div className="mt-4 font-display text-xl font-extrabold tracking-wide text-orange-500">MeterFlow</div>
            <div className="mt-1 text-sm italic text-slate-500">"Radiants is my Pride"</div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="identifier" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email or Mobile Number</Label>
              <Input id="identifier" data-testid="login-identifier" type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required className="mt-1.5" placeholder="you@company.com or 98XXXXXXXX" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && <div data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In →"}
            </Button>
          </div>
          <p className="mt-8 text-center text-[11px] uppercase tracking-wide text-slate-400">© 2026 Radiants Civiletric Pvt Ltd</p>
        </form>
      </div>
    </div>
  );
}
